import { useMemo } from "react";
import ClockIcon from "../../../../../../../assets/svg/clock-icon";
import FlaggedIcon from "../../../../../../../assets/svg/flagged-icon";
import {
  getSlaDeadlineInfo,
  type SlaUrgency,
  type SlaDeadlineInfo,
} from "./getSlaDeadlineInfo";

interface SlaDeadlineTagProps {
  dueDate: string | null | undefined;
  variant?: "full" | "compact";
}

const URGENCY_STYLES: Record<
  SlaUrgency,
  { container: string; iconColor: string }
> = {
  default: {
    container:
      "border-border-gray dark:border-border-gray-dk",
    iconColor:
      "text-gray-placeholder dark:text-gray-placeholder-dk",
  },
  warning: {
    container:
      "bg-card-yellow-bg dark:bg-card-yellow-bg-dk border-card-yellow-stroke dark:border-card-yellow-stroke-dk",
    iconColor:
      "text-dark-yellow-for-alerts dark:text-dark-yellow-for-alerts-dk",
  },
  urgent: {
    container:
      "bg-tag-orange-bg dark:bg-tag-orange-bg-dk border-tag-orange-bg dark:border-tag-orange-bg-dk",
    iconColor: "text-[#F76808]",
  },
  breached: {
    container:
      "bg-tag-red-bg dark:bg-tag-red-bg-dk border-tag-red-stroke dark:border-tag-red-stroke-dk",
    iconColor: "text-verba-red-alert dark:text-verba-red-alert-dk",
  },
};

const renderIcon = (info: SlaDeadlineInfo, size: string) => {
  const style = URGENCY_STYLES[info.urgency];
  if (info.urgency === "breached") {
    return <FlaggedIcon className={`${size} shrink-0 ${style.iconColor}`} />;
  }
  return <ClockIcon className={`${size} shrink-0 ${style.iconColor}`} />;
};

const SlaDeadlineTag = ({
  dueDate,
  variant = "full",
}: SlaDeadlineTagProps) => {
  const info = useMemo(() => getSlaDeadlineInfo(dueDate), [dueDate]);

  if (!info) return null;

  const style = URGENCY_STYLES[info.urgency];

  if (variant === "compact") {
    return (
      <div
        className={`w-fit inline-flex items-center gap-0.5 px-1 py-0.5 rounded-md border ${style.container}`}
      >
        {renderIcon(info, "w-3.5 h-3.5")}
        <span className="text-sm font-medium text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap leading-normal">
          {info.shortLabel}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`w-fit inline-flex items-center gap-2 pl-1.5 pr-2 py-1 rounded-lg border ${style.container}`}
    >
      <div className="flex items-center gap-1">
        {renderIcon(info, "w-3.5 h-3.5")}
        <span className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
          {info.label}
        </span>
      </div>
      <div className="h-[10px] w-px bg-border-gray dark:bg-border-gray-dk shrink-0" />
      <span className="text-xs font-normal text-data dark:text-data-dk whitespace-nowrap leading-normal">
        {info.sublabel}
      </span>
    </div>
  );
};

export default SlaDeadlineTag;
